import React, { useCallback } from 'react';
import SuccessIcon from '@atlaskit/icon/core/migration/status-success--check-circle';
import ErrorIcon from '@atlaskit/icon/core/migration/status-error--error';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import AutoDismissFlag from '@atlassian/jira-flags/src/common/ui/components/ak-auto-dismiss-flag';
import FlagGroup from '@atlassian/jira-flags/src/common/ui/components/group-flag';
import { useIntl } from '@atlassian/jira-intl';
import { useFlagsService } from '@atlassian/jira-flags';
import messages from './messages.tsx';
import { getMessageId } from './utils.tsx';

export type Flags = {
	styleClass: string;
	id: number;
	isSuccessful: boolean;
}[];

type Props = {
	issueKey: string;
	flags: Flags;
	onDismiss: () => void;
};

export const useIssueOperationFlags = (issueKey: string) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();

	return useCallback(
		(styleClass: string, isSuccessful: boolean) => {
			const messageId =
				'issue-navigator-issue-operations.ui.notification-flag-group.show-flag.auto-dismiss-flag';
			const messageType = 'transactional';
			const isAutoDismiss = true;

			return isSuccessful
				? showFlag({
						messageId,
						messageType,
						isAutoDismiss,
						type: 'success',
						title: formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.successTitleIssueTerminologyRefreshM4
								: messages.successTitle,
						),
						description: formatMessage(messages[getMessageId(styleClass)], {
							issueKey,
						}),
					})
				: showFlag({
						messageId,
						messageType,
						isAutoDismiss,
						type: 'error',
						title: formatMessage(messages.errorTitle),
						description: formatMessage(messages.errorDesc, {
							issueKey,
						}),
					});
		},
		[formatMessage, issueKey, showFlag],
	);
};

// look into if we can clean this up with empanada_nin_convert_issue_ops_to_entrypoint
const NotificationFlagGroup = ({ flags, onDismiss, issueKey }: Props) => {
	const { formatMessage } = useIntl();
	return (
		<FlagGroup onDismissed={onDismiss}>
			{flags.map(({ id, styleClass, isSuccessful }) => (
				<AutoDismissFlag
					id={id}
					messageId="issue-navigator-issue-operations.ui.notification-flag-group.auto-dismiss-flag"
					messageType="transactional"
					icon={
						isSuccessful ? (
							<SuccessIcon
								label="Success"
								LEGACY_size="medium"
								spacing="spacious"
								color={token('color.icon.success')}
							/>
						) : (
							<ErrorIcon
								label="Error"
								LEGACY_size="medium"
								spacing="spacious"
								color={token('color.icon.danger')}
							/>
						)
					}
					key={id}
					title={
						isSuccessful
							? formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.successTitleIssueTerminologyRefreshM4
										: messages.successTitle,
								)
							: formatMessage(messages.errorTitle)
					}
					description={
						isSuccessful
							? formatMessage(messages[getMessageId(styleClass)], {
									issueKey,
								})
							: formatMessage(messages.errorDesc, {
									issueKey,
								})
					}
				/>
			))}
		</FlagGroup>
	);
};

export default NotificationFlagGroup;
